import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollText, Filter, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, BarChart3, Users, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { TitleWithHelp } from '../components/ui/help-tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const ACAO_LABELS = {
  create: 'Criação',
  update: 'Atualização',
  delete: 'Exclusão',
};

const ACAO_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};

const RECURSO_LABELS = {
  empresas: 'Empresas',
  cotacoes: 'Cotações',
  pedidos_compra: 'Pedidos de Compra',
  fornecedores: 'Fornecedores',
  produtos: 'Produtos',
  projetos: 'Projetos',
  producao: 'Produção',
  fabricas: 'Fábricas',
  perfis: 'Perfis',
  usuarios: 'Usuários',
  backup: 'Backup',
  storage: 'Storage',
  marcas: 'Marcas',
  embarcacoes: 'Embarcações',
  estoque: 'Estoque',
  tipos_servico: 'Tipos de Serviço',
  conta_azul: 'Conta Azul',
};

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [recursos, setRecursos] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const limit = 30;

  const [filtros, setFiltros] = useState({
    recurso: '',
    acao: '',
    usuario_id: '',
    data_inicio: '',
    data_fim: '',
  });

  useEffect(() => {
    document.title = "Logs de Auditoria — NautilusOS";
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, filtros]);

  const loadFilterOptions = async () => {
    try {
      const [recursosRes, acoesRes, usuariosRes, statsRes] = await Promise.all([
        api.get('/logs/recursos'),
        api.get('/logs/acoes'),
        api.get('/logs/usuarios'),
        api.get('/logs/estatisticas?dias=30'),
      ]);
      setRecursos(recursosRes.data);
      setAcoes(acoesRes.data);
      setUsuarios(usuariosRes.data);
      setStats(statsRes.data);
    } catch {
      // Silenciar - filtros podem estar vazios inicialmente
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (filtros.recurso) params.append('recurso', filtros.recurso);
      if (filtros.acao) params.append('acao', filtros.acao);
      if (filtros.usuario_id) params.append('usuario_id', filtros.usuario_id);
      if (filtros.data_inicio) params.append('data_inicio', new Date(filtros.data_inicio).toISOString());
      if (filtros.data_fim) params.append('data_fim', new Date(filtros.data_fim).toISOString());

      const res = await api.get(`/logs?${params.toString()}`);
      setLogs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFiltros({ recurso: '', acao: '', usuario_id: '', data_inicio: '', data_fim: '' });
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="logs-page">
      <div className="flex justify-between items-center">
        <div>
          <TitleWithHelp
            title="Logs de Auditoria"
            description="Visualize todas as ações realizadas no sistema. Filtre por recurso, ação, usuário ou período para rastrear alterações."
            connections={['Perfis de Acesso', 'Usuários']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="logs-title">
              <ScrollText className="h-6 w-6 text-blue-600" />
              Logs de Auditoria
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Histórico de ações do sistema</p>
        </div>
        <Button onClick={() => { loadLogs(); loadFilterOptions(); }} variant="outline" data-testid="logs-refresh-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="logs-stats">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><BarChart3 className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.total_acoes}</p>
                <p className="text-xs text-gray-500">Ações (30 dias)</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.total_erros}</p>
                <p className="text-xs text-gray-500">Erros</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100"><Users className="h-5 w-5 text-emerald-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.por_usuario?.length || 0}</p>
                <p className="text-xs text-gray-500">Usuários ativos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><FileText className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.por_recurso?.length || 0}</p>
                <p className="text-xs text-gray-500">Recursos monitorados</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card data-testid="logs-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Recurso</Label>
              <Select value={filtros.recurso} onValueChange={(v) => { setFiltros(p => ({...p, recurso: v === '_all' ? '' : v})); setPage(0); }}>
                <SelectTrigger data-testid="filter-recurso"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  {recursos.map(r => (
                    <SelectItem key={r} value={r}>{RECURSO_LABELS[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ação</Label>
              <Select value={filtros.acao} onValueChange={(v) => { setFiltros(p => ({...p, acao: v === '_all' ? '' : v})); setPage(0); }}>
                <SelectTrigger data-testid="filter-acao"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  {acoes.map(a => (
                    <SelectItem key={a} value={a}>{ACAO_LABELS[a] || a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Usuário</Label>
              <Select value={filtros.usuario_id} onValueChange={(v) => { setFiltros(p => ({...p, usuario_id: v === '_all' ? '' : v})); setPage(0); }}>
                <SelectTrigger data-testid="filter-usuario"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  {usuarios.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data Início</Label>
              <Input type="date" value={filtros.data_inicio} onChange={(e) => { setFiltros(p => ({...p, data_inicio: e.target.value})); setPage(0); }} data-testid="filter-data-inicio" />
            </div>
            <div>
              <Label className="text-xs">Data Fim</Label>
              <Input type="date" value={filtros.data_fim} onChange={(e) => { setFiltros(p => ({...p, data_fim: e.target.value})); setPage(0); }} data-testid="filter-data-fim" />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">Limpar Filtros</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Logs */}
      <Card data-testid="logs-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Data/Hora</th>
                  <th className="text-left p-3 font-medium text-gray-600">Usuário</th>
                  <th className="text-left p-3 font-medium text-gray-600">Ação</th>
                  <th className="text-left p-3 font-medium text-gray-600">Recurso</th>
                  <th className="text-left p-3 font-medium text-gray-600">Descrição</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-500">Carregando...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-400">
                    <ScrollText className="mx-auto h-8 w-8 mb-2 opacity-50" />
                    Nenhum log encontrado
                  </td></tr>
                ) : logs.map((log, idx) => (
                  <tr key={log.id || idx} className="hover:bg-gray-50" data-testid={`log-row-${idx}`}>
                    <td className="p-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Clock className="h-3.5 w-3.5" />
                        {formatDate(log.timestamp)}
                      </div>
                    </td>
                    <td className="p-3">{log.usuario_nome || log.usuario_id || '-'}</td>
                    <td className="p-3">
                      <Badge className={`text-xs ${ACAO_COLORS[log.acao] || 'bg-gray-100 text-gray-700'}`}>
                        {ACAO_LABELS[log.acao] || log.acao}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <span className="text-gray-700">{RECURSO_LABELS[log.recurso] || log.recurso}</span>
                    </td>
                    <td className="p-3 max-w-xs truncate text-gray-600">{log.descricao || '-'}</td>
                    <td className="p-3">
                      {log.sucesso !== false ? (
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-400">{log.ip_address || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t" data-testid="logs-pagination">
              <span className="text-sm text-gray-500">
                {page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total} registros
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="flex items-center px-2 text-sm">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  variant="outline" size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="pagination-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LogsPage;
